import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress, Link as MuiLink,
} from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasMarketplaceOnboarding } from '../services/marketplaceOnboardingService';

export default function LoginPage() {
  const { signIn, handleNewPassword, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fromMarketplace = new URLSearchParams(location.search).get('fromMarketplace') === '1';
  const alreadyRegistered = new URLSearchParams(location.search).get('alreadyRegistered') === '1';
  const initialEmail = new URLSearchParams(location.search).get('email') || '';
  const [newPwChallenge, setNewPwChallenge] = useState(null);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (initialEmail) {
      setUsername(initialEmail);
    }
  }, [initialEmail]);

  useEffect(() => {
    if (fromMarketplace && !alreadyRegistered && !hasMarketplaceOnboarding()) {
      setError('Marketplace session not found. Restart the AWS Marketplace setup flow or sign in normally.');
    }
  }, [fromMarketplace, alreadyRegistered]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await signIn(username, password);
      if (result.newPasswordRequired) {
        setNewPwChallenge(result);
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await handleNewPassword(newPwChallenge.cognitoUser, newPassword, newPwChallenge.userAttributes);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  if (newPwChallenge) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <Card sx={{ maxWidth: 400, width: '100%', p: 2 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>Set New Password</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <form onSubmit={handleNewPasswordSubmit}>
              <TextField
                label="New Password" type="password" fullWidth margin="normal"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
              />
              <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Set Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Card sx={{ maxWidth: 400, width: '100%', p: 2 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>Sign In</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>VIC Services API Management</Typography>
          {fromMarketplace && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {alreadyRegistered
                ? 'Your AWS Marketplace account is already linked. Sign in to continue.'
                : 'Marketplace validation is complete. Sign in if you already have an account.'}
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            <TextField
              label="Email / Username" fullWidth margin="normal"
              value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus
            />
            <TextField
              label="Password" type="password" fullWidth margin="normal"
              value={password} onChange={(e) => setPassword(e.target.value)} required
            />
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </form>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              <MuiLink component={Link} to="/forgot-password">Forgot password?</MuiLink>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Don't have an account?{' '}
              <MuiLink component={Link} to={fromMarketplace ? '/register?fromMarketplace=1' : '/register'}>Register here</MuiLink>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
